import React, { useMemo, useState } from "react";
import { motion } from "motion/react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import { NavLink } from "react-router-dom";
import classNames from "classnames";
import BjelinLogo from "../BjelinLogo";
import ImageOrVideo from "../ImageOrVideo";
import { unwrapAsset } from "../../utils/CockpitUtils";
import Icon from "../Icon";
import {
  useCategory,
  useRootCategories,
  useUserProducts
} from "../../hooks/ProductHooks";
import {
  categoryIsListable,
  getProductCategoryPedigree,
  productCategoryHasParent,
  productCategoryUrl
} from "../../utils/ProductCategoryUtils";
import "./style.scss";
import { useUser } from "../../functional-components/UserContext";
import { ProductCategory } from "../../types/ProductCategory";
import { Product } from "../../types/Product";

const CategoryLink = ({
  category,
  currentCat,
  withProducts
}: {
  category: ProductCategory;
  currentCat?: ProductCategory;
  withProducts?: Product[];
}) => {
  const { key, short_key, title, children } = category;
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const currentCategoryIsChildOfCategory = productCategoryHasParent(
    currentCat,
    category,
    true
  );
  const [expanded, setExpanded] = useState(
    currentCategoryIsChildOfCategory || currentCat?.key === key
  );
  const selected = key === currentCat?.key;
  const { admin } = useUser();
  const isListable = useMemo(() => {
    return categoryIsListable(category, withProducts);
  }, [withProducts, category]);
  const muted = admin && !isListable;

  return admin || isListable ? (
    <li
      role="treeitem"
      aria-selected={selected}
      aria-expanded={children.length > 0 ? expanded : undefined}
      className={classNames({
        selected,
        expanded,
        muted
      })}
    >
      {children.length === 0 ? (
        <NavLink to={productCategoryUrl(category) || ""} tabIndex={-1}>
          <div title={t("action.select")} className="toggle-icon">
            {selected && <Icon name={"circle"} type={"fas"} />}
          </div>
          <span className="category-name">
            {short_key === "outlet" || short_key === "concept" ? (
              <BjelinLogo
                variant={short_key}
                style={{ marginTop: "0.8em", verticalAlign: "-0.08em" }}
              />
            ) : (
              title || tp(`categories.${short_key}`)
            )}
          </span>
        </NavLink>
      ) : (
        <>
          <NavLink
            className={"supercategory"}
            key={short_key}
            to={productCategoryUrl(category) || ""}
            onClick={_e => {
              setExpanded(expanded => !expanded || !selected);
            }}
            tabIndex={-1}
          >
            <button
              title={
                expanded ? t("action.close") : t("action.expand-subcategories")
              }
              className="toggle-icon"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setExpanded(expanded => !expanded);
              }}
            >
              {selected && <Icon name={"circle"} type={"fas"} />}
              <Icon name={expanded ? "minus" : "plus"} type={"far"} />
            </button>
            <span className="category-name">
              {title || tp(`categories.${short_key}`)}
            </span>
          </NavLink>

          <motion.div
            role="group"
            animate={{ height: expanded ? "auto" : 0 }}
            className={"subcategories"}
          >
            <ul>
              {children.map(category => (
                <CategoryLink
                  category={category}
                  currentCat={currentCat}
                  key={category._id}
                  withProducts={withProducts}
                />
              ))}
            </ul>
          </motion.div>
        </>
      )}
    </li>
  ) : null;
};

const CategoryLinks = ({
  categoryId,
  expandedOnInit = false
}: {
  categoryId?: string;
  expandedOnInit?: boolean;
}) => {
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const products = useUserProducts();
  const rootCategories = useRootCategories();
  const currentCategory = useCategory(categoryId);
  const currentCategoryPedigree = getProductCategoryPedigree(currentCategory);
  const [expanded, setExpanded] = useState(expandedOnInit);
  let pathCharLength = 0;
  currentCategoryPedigree.forEach(category => {
    pathCharLength += (category?.title?.length || 0) + 5;
  });

  return (
    <div className={"category-links"} aria-hidden="true">
      <button
        className="category-filter-header"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded(expanded => !expanded);
        }}
      >
        <div className="category-filter-label">{tp("category")}:</div>
        <div
          className={classNames("category-filter-value", {
            "overflow-fade": pathCharLength > 32
          })}
        >
          <div className="category-filter-value-inner">
            {currentCategoryPedigree.map(cat => (
              <span key={cat._id}>
                {cat.title}{" "}
                {cat !== currentCategory && (
                  <>
                    <Icon name="chevron-right" />{" "}
                  </>
                )}
              </span>
            ))}
          </div>
        </div>
        <div
          title={expanded ? t("action.close") : t("action.expand")}
          className="toggle-icon"
        >
          <Icon name={expanded ? "chevron-up" : "chevron-down"} />
        </div>
      </button>

      <motion.div
        animate={{ height: expanded ? "auto" : 0 }}
        className={"categories-collapse"}
      >
        <ul role="tree">
          {rootCategories.map((category, idx) => (
            <CategoryLink
              category={category}
              currentCat={currentCategory}
              withProducts={products}
              key={idx}
            />
          ))}
        </ul>
      </motion.div>
    </div>
  );
};

export default CategoryLinks;

export const CategorySubTreeNav = ({
  category,
  setExpandedCat
}: {
  category: ProductCategory;
  setExpandedCat?: (categoryKey: string | undefined) => void;
}) => {
  const products = useUserProducts();

  return (
    <ul className="category-subtree-nav">
      {category.children.map(
        ch =>
          categoryIsListable(ch, products) && (
            <li key={ch._id}>
              <NavLink
                to={productCategoryUrl(ch) || ""}
                onClick={_e => {
                  setExpandedCat?.(undefined);
                }}
              >
                <div className="image">
                  <ImageOrVideo
                    image={
                      ch.shop_herobox_background_image
                        ? unwrapAsset(ch.shop_herobox_background_image)
                        : undefined
                    }
                  />
                </div>
                <div className="title">{ch.title}</div>
              </NavLink>
            </li>
          )
      )}
    </ul>
  );
};

export const CategoryNav = ({
  curCat,
  categories
}: {
  curCat: ProductCategory;
  categories: ProductCategory[];
}) => {
  const _t = useBjelinTranslations();
  const tp = useProductTranslations();
  const products = useUserProducts();

  return (
    <nav className="category-nav" aria-label={tp("categories")}>
      <ul>
        {categories.map(
          (category, idx) =>
            categoryIsListable(category, products) && (
              <li
                key={idx}
                className={classNames({
                  active: productCategoryHasParent(curCat, category, true)
                })}
              >
                <NavLink
                  id={"category-nav-" + category.short_key}
                  to={productCategoryUrl(category) || ""}
                  onClick={_e => {
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className="image">
                    <ImageOrVideo
                      image={
                        category.shop_herobox_background_image
                          ? unwrapAsset(category.shop_herobox_background_image)
                          : undefined
                      }
                    />
                  </div>
                  <div className="title">{category.title}</div>
                </NavLink>
              </li>
            )
        )}
      </ul>
    </nav>
  );
};
